import React, { useState } from 'react';
import config from '../../config';

const AVATAR_GRADIENTS = [
  'from-orange-500 to-amber-500 ring-orange-200/50 shadow-orange-500/15',
  'from-blue-600 to-indigo-600 ring-blue-200/50 shadow-blue-500/15',
  'from-emerald-500 to-teal-600 ring-emerald-200/50 shadow-emerald-500/15',
  'from-rose-500 to-pink-600 ring-rose-200/50 shadow-rose-500/15',
  'from-purple-600 to-violet-600 ring-purple-200/50 shadow-purple-500/15',
  'from-amber-500 to-orange-600 ring-amber-200/50 shadow-amber-500/15',
  'from-cyan-500 to-blue-600 ring-cyan-200/50 shadow-cyan-500/15',
  'from-fuchsia-500 to-rose-600 ring-fuchsia-200/50 shadow-fuchsia-500/15',
];

export const getAvatarInitials = (name = '', avatar = null) => {
  // If avatar is already 1-3 letters of initials (e.g., "PG", "SK"), use that
  if (avatar && typeof avatar === 'string') {
    const trimmedAvatar = avatar.trim();
    if (trimmedAvatar.length <= 3 && !trimmedAvatar.includes('.') && !trimmedAvatar.includes('/')) {
      return trimmedAvatar.toUpperCase();
    }
  }

  // If name is provided, generate initials
  if (name && typeof name === 'string') {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    // First letter of first word + first letter of last word (e.g., "Pranali Girme" -> "PG")
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  return '?';
};

export const getAvatarGradient = (seed = '') => {
  if (!seed) return AVATAR_GRADIENTS[0];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
};

const AvatarImage = ({ 
  src, 
  name, 
  size = 'md', 
  className = '', 
  fallbackType = 'initials' // 'initials' (default), 'placeholder', or 'dicebear'
}) => {
  const [imageError, setImageError] = useState(false);

  const sizeClasses = {
    xs: 'w-6 h-6 min-w-[24px]',
    sm: 'w-8 h-8 min-w-[32px]',
    md: 'w-10 h-10 min-w-[40px]',
    lg: 'w-14 h-14 min-w-[56px]',
    xl: 'w-20 h-20 min-w-[80px]'
  };

  const textSizes = {
    xs: 'text-[10px]',
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-lg',
    xl: 'text-2xl'
  };

  // Determine if src is a raw initials string (like "PG", "S", "KP")
  const isRawInitials = typeof src === 'string' && src.trim().length <= 3 && !src.includes('.') && !src.includes('/');

  // Normalize image URL
  const getNormalizedSrc = () => {
    if (!src || typeof src !== 'string') return null;
    if (isRawInitials) return null;
    // Discard old dicebear URLs so clean initials are shown instead of cartoon faces
    if (src.includes('dicebear.com')) return null;

    if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('data:') || src.startsWith('blob:')) {
      return src;
    }
    if (src.startsWith('/uploads') || src.startsWith('avatar_')) {
      const cleanPath = src.startsWith('/') ? src : `/uploads/avatars/${src}`;
      return `${config.url}${cleanPath}`;
    }
    if (/\.(jpg|jpeg|png|webp|gif|svg)$/i.test(src)) {
      return `${config.url}/uploads/avatars/${src}`;
    }
    return null;
  };

  const imageSrc = getNormalizedSrc();
  const shouldShowImage = Boolean(imageSrc) && !imageError;
  const initials = getAvatarInitials(name, src);
  const gradientClass = getAvatarGradient(name || initials);

  return (
    <div 
      className={`${sizeClasses[size] || sizeClasses.md} rounded-full overflow-hidden flex-shrink-0 select-none relative ${className}`}
    >
      {shouldShowImage ? (
        <img
          src={imageSrc}
          alt={name || 'Avatar'}
          className="w-full h-full object-cover rounded-full"
          onError={() => setImageError(true)}
        />
      ) : (
        <div 
          className={`w-full h-full bg-gradient-to-br ${gradientClass} flex items-center justify-center font-bold text-white tracking-wider shadow-inner ring-1 ring-white/30 ${textSizes[size] || textSizes.md}`}
          title={name || initials}
        >
          {initials}
        </div>
      )}
    </div>
  );
};

export default AvatarImage;